import {Employee} from "./Employee";
import {Address} from "./Address";
import {BankAccount} from "./BankAccount";
import {IssuerStatus, PersonalizationType, WebChargeInterval} from "@/Enums"
import {IssuerDto} from "./IssuerDto";
import {Logo} from "./Logo";
import {Contract} from "./Contract";
import {BankAccountDto} from "@/model/BankAccountDto";
import {PaymentDetails} from "@/model/PaymentDetails";
import {PaymentDetailsDto} from "@/model/PaymentDetailsDto";

export class Issuer {
  private issuerModel: IssuerDto;

  public CompanyName: string | undefined = undefined;
  public ContactEmployeeId: number | undefined = undefined;
  public ContactEmployee: Employee = new Employee();
  public ContactAddressId: number | undefined = undefined;
  public ContactAddress: Address = new Address();
  public BusinessAddressId: number | undefined = undefined;
  public BusinessAddress: Address = new Address();
  public Status: IssuerStatus | undefined = undefined;
  public InterestedNumber: number | undefined = undefined;
  public BusinessBankAccountId: number | undefined = undefined;
  public BusinessBankAccount: BankAccount = new BankAccount();
  public IsWebsitePartner: boolean = false;
  public WebsiteUrl: string | undefined = undefined;
  public IsMerchant: boolean = false;
  public PaycardCount: number = 0;
  public WebChargeInterval: WebChargeInterval | undefined = undefined;
  public PersonalizePaycard: boolean = false;
  public PersonalizationType: PersonalizationType | undefined = undefined;
  public WebsiteLogoId: number | undefined = undefined;
  public WebsiteLogo: Logo = new Logo();
  public PaycardLogoId: number | undefined = undefined;
  public PaycardLogo: Logo = new Logo();
  public PaycardText: string | undefined = undefined;
  public DebitorId: string | undefined = undefined;
  public Contract: Contract = new Contract();
  public IssuerCardNumber: number | undefined = undefined;
  public PersonalizedLogoId: number | undefined = undefined;
  public PersonalizedLogo: Logo = new Logo();
  public PaymentDetailsId: number | undefined;
  public PaymentDetails: PaymentDetails = new PaymentDetails(new PaymentDetailsDto());

  constructor(issuer?: IssuerDto) {
    this.issuerModel = issuer ?? new IssuerDto();
    this.SetValues(this.issuerModel);
  }

  public Reset() {
    this.SetValues(this.issuerModel);
  }

  get HasChanges() {
    return this.issuerModel.id == undefined
      || this.CompanyName !== this.issuerModel.companyName
      || this.ContactEmployeeId !== this.issuerModel.contactEmployeeId
      || this.ContactEmployee?.HasChanges
      || this.ContactAddressId !== this.issuerModel.contactAddressId
      || this.ContactAddress?.HasChanges
      || this.BusinessAddressId !== this.issuerModel.businessAddressId
      || this.BusinessAddress?.HasChanges
      || this.Status !== this.issuerModel.status
      || this.InterestedNumber !== this.issuerModel.interestedNumber
      || this.BusinessBankAccountId !== this.issuerModel.businessBankAccountId
      || this.BusinessBankAccount?.HasChanges
      || this.IsWebsitePartner !== this.issuerModel.isWebsitePartner
      || this.WebsiteUrl !== this.issuerModel.websiteUrl
      || this.IsMerchant !== this.issuerModel.isMerchant
      || this.PaycardCount !== this.issuerModel.paycardCount
      || this.WebChargeInterval !== this.issuerModel.webChargeInterval
      || this.PersonalizePaycard !== this.issuerModel.personalizePaycard
      || this.PersonalizationType !== this.issuerModel.personalizationType
      || this.WebsiteLogo.HasChanges
      || this.PaycardLogo.HasChanges
      || this.PersonalizedLogo.HasChanges
      || this.PaycardText !== this.issuerModel.paycardText
      || this.DebitorId !== this.issuerModel.debitorId
      || this.Contract?.HasChanges
      || this.IssuerCardNumber !== this.issuerModel.cardNumber
      || this.PaymentDetails.HasChanges;
  }

  public GetModel() {
    let model = new IssuerDto();

    model.id = this.issuerModel.id;
    model.companyName = this.CompanyName;
    model.contactEmployeeId = this.ContactEmployeeId;
    model.contactEmployee = this.ContactEmployee.GetModel();
    model.contactAddressId = this.ContactAddressId;
    model.contactAddress = this.ContactAddress.GetModel();
    model.businessAddressId = this.BusinessAddressId;
    model.businessAddress = this.BusinessAddress.GetModel();
    model.status = this.Status;
    model.interestedNumber = this.InterestedNumber;
    model.businessBankAccountId = this.BusinessBankAccountId;
    model.businessBankAccount = this.BusinessBankAccount.isModelEmpty()
      ? undefined
      : this.BusinessBankAccount.GetModel();
    model.isWebsitePartner = this.IsWebsitePartner;
    model.websiteUrl = this.WebsiteUrl;
    model.isMerchant = this.IsMerchant;
    model.paycardCount = this.PaycardCount;
    model.webChargeInterval = this.WebChargeInterval;
    model.personalizePaycard = this.PersonalizePaycard;
    model.personalizationType = this.PersonalizationType;
    model.websiteLogoId = this.WebsiteLogoId;
    model.websiteLogo = this.WebsiteLogo.GetModel();
    model.paycardLogoId = this.PaycardLogoId;
    model.paycardLogo = this.PaycardLogo.GetModel();
    model.paycardText = this.PaycardText;
    model.debitorId = this.DebitorId;
    model.contractStart = this.Contract.Start;
    model.contractEnd = this.Contract.End;
    model.cardNumber = this.IssuerCardNumber;
    model.personalizedLogoId = this.PersonalizedLogoId;
    model.personalizedLogo = this.PersonalizedLogo.GetModel();
    model.paymentDetailsId = this.PaymentDetailsId;
    model.paymentDetails = this.PaymentDetails.GetModel();

    return model;
  }

  get IsNew() {
    return this.issuerModel.id === undefined;
  }

  get Id() {
    return this.issuerModel.id;
  }

  public SetValues(issuer: IssuerDto) {
    this.issuerModel = issuer;
    this.ContactEmployee.SetValues(issuer.contactEmployee);
    this.ContactAddress.SetValues(issuer.contactAddress);
    this.BusinessAddress.SetValues(issuer.businessAddress);
    this.BusinessBankAccount.SetValues(issuer.businessBankAccount ? issuer.businessBankAccount : new BankAccountDto());
    this.WebsiteLogoId = issuer.websiteLogoId;
    this.WebsiteLogo.SetValues(issuer.websiteLogo);
    this.PaycardLogoId = issuer.paycardLogoId;
    this.PaycardLogo.SetValues(issuer.paycardLogo);
    this.Contract.SetValues(issuer.contractStart, issuer.contractEnd);
    this.CompanyName = issuer.companyName;
    this.ContactEmployeeId = issuer.contactEmployeeId;
    this.ContactAddressId = issuer.contactAddressId;
    this.BusinessAddressId = issuer.businessAddressId;
    this.Status = issuer.status;
    this.InterestedNumber = issuer.interestedNumber;
    this.BusinessBankAccountId = issuer.businessBankAccountId;
    this.IsWebsitePartner = issuer.isWebsitePartner;
    this.WebsiteUrl = issuer.websiteUrl;
    this.IsMerchant = issuer.isMerchant;
    this.PaycardCount = issuer.paycardCount;
    this.WebChargeInterval = issuer.webChargeInterval;
    this.PersonalizePaycard = issuer.personalizePaycard;
    this.PersonalizationType = issuer.personalizationType;
    this.PaycardText = issuer.paycardText;
    this.DebitorId = issuer.debitorId;
    this.issuerModel.cardNumber = this.IssuerCardNumber = issuer.cardNumber ? issuer.cardNumber : undefined;
    this.PersonalizedLogoId = issuer.personalizedLogoId;
    this.PersonalizedLogo.SetValues(issuer.personalizedLogo);
    this.PaymentDetailsId = issuer.paymentDetailsId;
    this.PaymentDetails.SetValues(issuer.paymentDetails ? issuer.paymentDetails : new PaymentDetailsDto());
  }

  public HasChangedPaymentMethod() : boolean {
    return this.PaymentDetails.HasChanges;
  }

}

